<!-- manage-response-detail-form.component.html -->

<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [dismissableMask]="false"
  styleClass="generic-modal-container create-rca-dialog"
  (onHide)="close()"
  [draggable]="false"
  [resizable]="false"
  [closable]="false"
>
  <!-- Single Header Template for the entire multi-step dialog -->
  <ng-template pTemplate="header">
    <!-- Title changes based on page -->
    <h2 *ngIf="showFirstPage">Regulatory Change Assessment Details</h2>
    <h2 *ngIf="showNextPage">Response to RCA</h2>
    <button class="close-button" (click)="close()">
      <i class="pi pi-times"></i>
    </button>
  </ng-template>

  <!-- Single Content Area for the entire multi-step dialog -->
  <div class="modal-body">
    <!-- First Form Page Content -->
    <div *ngIf="showFirstPage">
      <form [formGroup]="rcaForm" autocomplete="off">
        <div class="form-grid-container" [class.has-other-design-docs]="showOtherDesignDocsField">
          <!-- ... (Your existing first page form fields) ... -->
          <!-- Column 1 & 2 Fields (Left Side - 6 Rows) -->
          <!-- Row 1 -->
          <div class="form-group">
            <label for="title">Title <span class="required">*</span></label>
            <input
              id="title"
              type="text"
              pInputText
              placeholder="Enter Title"
              formControlName="title"
              [readonly]="rcaForm.disabled"
            />
          </div>

          <div class="form-group">
            <label for="region">Region <span class="required">*</span></label>
            <!-- Display selected regions when form is disabled -->
            <div *ngIf="rcaForm.disabled" class="readonly-field-display">
              <span class="readonly-chip" *ngFor="let region of rcaForm.get('region')?.value" 
                    [pTooltip]="shouldShowTooltip(region?.name) ? region?.name : ''" 
                    tooltipPosition="top">
                {{ region?.name }}
              </span>
            </div>
            <!-- Show multiselect when form is enabled -->
            <p-multiSelect
              *ngIf="!rcaForm.disabled"
              id="region"
              [options]="regions"
              optionLabel="name"
              [filter]="true"
              formControlName="region"
              placeholder="Select Region(s)"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['region'].invalid }"
              display="chip"
              [maxSelectedLabels]="3"
              selectedItemsLabel="{0} regions selected"
            ></p-multiSelect>
          </div>
          <div class="form-group">
            <label for="usMedDeviceClass">US Med Device Class <span class="required">*</span></label>
            <p-dropdown
              id="usMedDeviceClass"
              [options]="usMedDeviceOptions"
              optionLabel="name"
              [filter]="true"
              formControlName="usMedDeviceClass"
              placeholder="Select US Med Device Class"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['usMedDeviceClass'].invalid }"
              [disabled]="rcaForm.disabled"
            ></p-dropdown>
          </div>

          <!-- Row 2 -->
          <div class="form-group">
            <label for="country">Country <span class="required">*</span></label>
            <!-- Display selected countries when form is disabled -->
            <div *ngIf="rcaForm.disabled" class="readonly-field-display">
              <span class="readonly-chip" *ngFor="let country of rcaForm.get('country')?.value" 
                    [pTooltip]="shouldShowTooltip(country?.name) ? country?.name : ''" 
                    tooltipPosition="top">
                {{ country?.name }}
              </span>
            </div>
            <!-- Show multiselect when form is enabled -->
            <p-multiSelect
              *ngIf="!rcaForm.disabled"
              id="country"
              [options]="countries"
              optionLabel="name"
              [filter]="true"
              formControlName="country"
              placeholder="Select Countries"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['country'].invalid }"
              display="chip"
              [maxSelectedLabels]="3"
              selectedItemsLabel="{0} countries selected"
            ></p-multiSelect>
          </div>
          <div class="form-group">
            <label for="usClassification">US Classification <span class="required">*</span></label>
            <p-dropdown
              id="usClassification"
              [options]="usClassifications"
              optionLabel="name"
              [filter]="true"
              formControlName="usClassification"
              placeholder="Select US Classification"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['usClassification'].invalid }"
              [disabled]="rcaForm.disabled"
            ></p-dropdown>
          </div>

          <!-- Row 3 -->

          <div class="form-group">
            <label for="ecrnEcnNumber">ECRN/ECN Number (optional)</label>
            <input
              id="ecrnEcnNumber"
              type="text"
              pInputText
              placeholder="Enter ECRN/ECN Number"
              formControlName="ecrnEcnNumber"
              [readonly]="rcaForm.disabled"
            />
          </div>
          <div class="form-group">
            <label for="euMedDeviceClass">EU Med Device Class <span class="required">*</span></label>
            <p-dropdown
              id="euMedDeviceClass"
              [options]="euMedDeviceOptions"
              optionLabel="name"
              [filter]="true"
              formControlName="euMedDeviceClass"
              placeholder="Select EU Med Device Class"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['euMedDeviceClass'].invalid }"
              [disabled]="rcaForm.disabled"
            ></p-dropdown>
          </div>

          <!-- Row 4 -->
          <div class="form-group">
            <label for="typeOfChange">Type of Change <span class="required">*</span></label>
            <!-- Display selected types when form is disabled -->
            <div *ngIf="rcaForm.disabled" class="readonly-field-display">
              <span class="readonly-chip" *ngFor="let type of rcaForm.get('typeOfChange')?.value" 
                    [pTooltip]="shouldShowTooltip(type?.name) ? type?.name : ''" 
                    tooltipPosition="top">
                {{ type?.name }}
              </span>
            </div>
            <!-- Show multiselect when form is enabled -->
            <p-multiSelect
              *ngIf="!rcaForm.disabled"
              id="typeOfChange"
              [options]="typeOfChanges"
              optionLabel="name"
              [filter]="true"
              formControlName="typeOfChange"
              placeholder="Select Types of Change"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['typeOfChange'].invalid }"
              display="chip"
              [maxSelectedLabels]="3"
              selectedItemsLabel="{0} types selected"
            ></p-multiSelect>
          </div>
          <div class="form-group">
            <label for="euClassification">EU Classification <span class="required">*</span></label>
            <p-dropdown
              id="euClassification"
              [options]="euClassifications"
              optionLabel="name"
              [filter]="true"
              formControlName="euClassification"
              placeholder="Select EU Classification"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['euClassification'].invalid }"
              [disabled]="rcaForm.disabled"
            ></p-dropdown>
          </div>

          <!-- Row 5 -->
          <div class="form-group">
            <label for="productFamily">Product Family <span class="required">*</span></label>
            <!-- Display selected product families when form is disabled -->
            <div *ngIf="rcaForm.disabled" class="readonly-field-display">
              <span class="readonly-chip" *ngFor="let family of rcaForm.get('productFamily')?.value" 
                    [pTooltip]="shouldShowTooltip(family?.name) ? family?.name : ''" 
                    tooltipPosition="top">
                {{ family?.name }}
              </span>
            </div>
            <!-- Show multiselect when form is enabled -->
            <p-multiSelect
              *ngIf="!rcaForm.disabled"
              id="productFamily"
              [options]="productFamilies"
              optionLabel="name"
              [filter]="true"
              formControlName="productFamily"
              placeholder="Select Product Families"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['productFamily'].invalid }"
              display="chip"
              [maxSelectedLabels]="3"
              selectedItemsLabel="{0} families selected"
            ></p-multiSelect>
          </div>
          <div class="form-group">
            <label for="issuedDate">Issued Date <span class="required">*</span></label>
            <p-calendar
              id="issuedDate"
              formControlName="issuedDate"
              [showIcon]="true"
              dateFormat="yy-mm-dd"
              placeholder="Select Issued Date"
              [appendTo]="'body'"
              [readonlyInput]="true"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['issuedDate'].invalid }"
              [disabled]="rcaForm.disabled"
            ></p-calendar>
          </div>

          <!-- Row 6 -->
          <div class="form-group">
            <label for="catalogueNumber">Catalogue Number <span class="required">*</span></label>
            <input
              id="catalogueNumber"
              type="text"
              pInputText
              placeholder="Enter Catalogue Number"
              formControlName="catalogueNumber"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['catalogueNumber'].invalid }"
              [readonly]="rcaForm.disabled"
            />
          </div>
          <div class="form-group">
            <label for="dueDate">Due Date <span class="required">*</span></label>
            <p-calendar
              id="dueDate"
              formControlName="dueDate"
              [showIcon]="true"
              dateFormat="yy-mm-dd"
              placeholder="Select Due Date"
              [appendTo]="'body'"
              [readonlyInput]="true"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['dueDate'].invalid }"
              [disabled]="rcaForm.disabled"
            ></p-calendar>
          </div>

          <!-- Column 3 Fields (Right Side - Spanning) -->
          <div class="form-group product-design-history-group">
            <label for="productDesignHistoryDocumentation">Product Design History Documentation <span class="required">*</span></label>
            <!-- Display selected documents when form is disabled -->
            <div *ngIf="rcaForm.disabled" class="readonly-field-display">
              <span class="readonly-chip" *ngFor="let doc of rcaForm.get('productDesignHistoryDocumentation')?.value" 
                    [pTooltip]="shouldShowTooltip(doc?.name, 40) ? doc?.name : ''" 
                    tooltipPosition="top">
                {{ doc?.name }}
              </span>
            </div>
            <!-- Show multiselect when form is enabled -->
            <p-multiSelect
              *ngIf="!rcaForm.disabled"
              id="productDesignHistoryDocumentation"
              [options]="productDesignHistoryOptions"
              optionLabel="name"
              [filter]="true"
              formControlName="productDesignHistoryDocumentation"
              placeholder="Select Product Design History Documentation"
              [appendTo]="'body'"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['productDesignHistoryDocumentation'].invalid }"
              display="chip"
              [maxSelectedLabels]="3"
              selectedItemsLabel="{0} documents selected"
            ></p-multiSelect>
          </div>

          <!-- Other Design Docs field - shown when "Other(s)" is selected -->
          <div class="form-group other-design-docs-group" *ngIf="showOtherDesignDocsField">
            <label for="otherDesignDocs">Other Design Docs <span class="required">*</span></label>
            <input
              id="otherDesignDocs"
              type="text"
              pInputText
              placeholder="Enter custom validation documentation and internal test reports"
              formControlName="otherDesignDocs"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['otherDesignDocs'].invalid }"
              [readonly]="rcaForm.disabled"
            />
          </div>

          <div class="form-group change-description-group">
            <label for="changeDescription">Change Description <span class="required">*</span></label>
            <!-- Read-only: framed chrome + maximize -->
            <div *ngIf="rcaForm.disabled" class="change-description-editor-shell change-description-readonly-in-shell">
              <div class="change-description-editor-chrome-header">
                <button
                  type="button"
                  pButton
                  icon="pi pi-window-maximize"
                  class="p-button-text p-button-rounded p-button-sm change-description-editor-chrome-btn"
                  pTooltip="Open maximized view"
                  tooltipPosition="left"
                  (click)="openChangeDescriptionReadonlyMax()"
                  aria-label="Open change description in maximized view"
                ></button>
              </div>
              <div
                class="change-description-display"
                role="textbox"
                aria-readonly="true"
                [innerHTML]="rcaForm.get('changeDescription')?.value"
              ></div>
            </div>
            <!-- Editable: chrome + maximize + full-screen editor dialog -->
            <div
              class="change-description-editor-shell"
              *ngIf="!rcaForm.disabled && !changeDescriptionMaxPreview"
              [class.change-description-editor-shell--invalid]="changeDescriptionInvalid"
            >
              <div class="change-description-editor-chrome-header">
                <button
                  type="button"
                  pButton
                  icon="pi pi-window-maximize"
                  class="p-button-text p-button-rounded p-button-sm change-description-editor-chrome-btn"
                  pTooltip="Open maximized editor"
                  tooltipPosition="left"
                  (click)="openChangeDescriptionMaxPreview()"
                  aria-label="Open change description in maximized editor"
                ></button>
              </div>
              <p-editor
                id="changeDescription"
                formControlName="changeDescription"
                [style]="{'height':'150px'}"
                placeholder="Enter the description for change assessment"
                [modules]="editorModules"
                [ngClass]="{ 'ng-invalid ng-dirty': changeDescriptionInvalid }"
              ></p-editor>
            </div>
          </div>

          <!-- Column 4 Fields (Right Side - Spanning) -->
          <div class="form-group planned-timeframe-group">
            <label for="plannedTimeframeForImplementation">Planned Timeframe for Implementation <span class="required">*</span></label>
            <input
              id="plannedTimeframeForImplementation"
              type="text"
              pInputText
              placeholder="Enter Planned Timeframe for Implementation"
              formControlName="plannedTimeframeForImplementation"
              [ngClass]="{ 'ng-invalid ng-dirty': formSubmitted && rcaForm.controls['plannedTimeframeForImplementation'].invalid }"
              [readonly]="rcaForm.disabled"
            />
          </div>

          <div class="form-group additional-attachments-group">
            <label for="additionalAttachments">Additional Attachments (optional)</label>
            <div class="attachment-upload-container">
              <textarea
                id="additionalAttachments"
                pInputTextarea
                rows="2"
                placeholder="Description about the files uploaded"
                formControlName="additionalAttachmentsDescription"
                class="attachment-description"
                [readonly]="rcaForm.disabled"
              ></textarea>
              <!-- Display all RCA uploaded files if available -->
              <div *ngIf="rcaUploadedFiles.length" class="attachments-list">
                <div class="attachment-item" *ngFor="let f of rcaUploadedFiles">
                  <i class="pi pi-file mr-2"></i>
                  <span class="file-name" [pTooltip]="f.name">{{ f.name }}</span>
                  <i class="pi {{ getRcaFileStatusInfo(f).icon }} ml-2 {{ getRcaFileStatusInfo(f).class }}"
                     [pTooltip]="getRcaFileStatusInfo(f).text"
                     *ngIf="getRcaFileStatusInfo(f).text"></i>
                  <button pButton type="button" icon="pi pi-download" class="p-button-text p-button-sm"
                    (click)="downloadRcaFile(f.url, f.name)"
                    [disabled]="!getRcaFileStatusInfo(f).canDownload"
                    [pTooltip]="getRcaFileStatusInfo(f).canDownload ? 'Download file' : 'File not available'"></button>
                </div>
              </div>
              <div *ngIf="!rcaUploadedFiles.length" class="no-files-placeholder">
                <small>No RCA attachments available.</small>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Second Form Page Content (Now just the component, no p-dialog) -->
    <div *ngIf="showNextPage" class="next-form-page-wrapper">
      <app-manage-response-next-form
        [mappingId]="mappingId"
        [isReviseCheck]="isReviseCheck"
        [reviseResponse]="reviseResponse"
        [rcaDataFromFirstPage]="formDataForNextPage.fullResponseData"
        (goBack)="goBackToFirstPage()"
        (formSubmittedEvent)="handleNextFormSubmission($event)"
        (saveFormEvent)="handleNextFormSave($event)"
      ></app-manage-response-next-form>
    </div>
  </div>

  <!-- Single Footer Template for the entire multi-step dialog -->
  <ng-template pTemplate="footer">
    <div class="footer-buttons">
      <button
        pButton
        type="button"
        label="Cancel"
        class="p-button-secondary"
        (click)="close()"
        *ngIf="showFirstPage"
      ></button>
      <button
        pButton
        type="button"
        label="Next"
        class="p-button-warning"
        (click)="goToNextPage()"
        *ngIf="showFirstPage"
      ></button>

      <!-- Buttons for the second page -->
      <ng-container *ngIf="showNextPage">
        <button pButton type="button" label="Save" class="p-button-secondary" (click)="saveNextForm()"></button>
        <button pButton type="submit" label="Submit" class="p-button-warning" (click)="submitNextForm()"></button>
      </ng-container>
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [(visible)]="changeDescriptionMaxPreview"
  [modal]="true"
  [dismissableMask]="true"
  [draggable]="false"
  [resizable]="false"
  [maximizable]="false"
  [showHeader]="false"
  [closable]="false"
  position="top"
  styleClass="change-description-max-dialog change-description-max-dialog--editor"
  [contentStyle]="changeDescriptionMaxDialogContentStyle"
>
  <div *ngIf="changeDescriptionMaxPreview" [formGroup]="rcaForm" class="change-description-max-editor-host">
    <div
      class="change-description-editor-shell change-description-editor-shell--max-dialog"
      [class.change-description-editor-shell--invalid]="changeDescriptionInvalid"
    >
      <div class="change-description-editor-chrome-header change-description-editor-chrome-header--dialog">
        <span class="change-description-editor-chrome-title">Change Description</span>
        <button
          type="button"
          pButton
          icon="pi pi-window-minimize"
          class="p-button-text p-button-rounded p-button-sm change-description-editor-chrome-btn"
          pTooltip="Exit maximized editor"
          tooltipPosition="left"
          (click)="closeChangeDescriptionMaxPreview()"
          aria-label="Exit maximized change description editor"
        ></button>
      </div>
      <p-editor
        id="changeDescriptionMaximized"
        formControlName="changeDescription"
        [style]="changeDescriptionMaxEditorStyle"
        placeholder="Enter the description for change assessment"
        [modules]="editorModules"
        [ngClass]="{ 'ng-invalid ng-dirty': changeDescriptionInvalid }"
      ></p-editor>
    </div>
  </div>
</p-dialog>

<p-dialog
  [(visible)]="changeDescriptionReadonlyMaxVisible"
  header="Change Description"
  [modal]="true"
  [dismissableMask]="true"
  [draggable]="false"
  [resizable]="false"
  [maximizable]="false"
  position="top"
  styleClass="change-description-max-dialog"
  [contentStyle]="changeDescriptionMaxDialogContentStyle"
>
  <div
    *ngIf="changeDescriptionReadonlyMaxVisible"
    class="change-description-display change-description-display--maximized"
    [innerHTML]="rcaForm.get('changeDescription')?.value"
  ></div>
</p-dialog>